// frontend/TwitterAnalytics/TwitterConnector.jsx
/**
 * Main Twitter Analytics Dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Tabs, Tab, Paper, Typography, Alert, Button,
  Divider, CircularProgress, Snackbar
} from '@mui/material';
import {
  Article as ContentIcon,
  People as UsersIcon,
  SentimentSatisfied as SentimentIcon,
  Warning as IssuesIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';

// Module Components
import TwitterConnectionStatus from './TwitterConnectionStatus';
import ContentAggregation from './ContentAggregation';
import UserAnalysis from './UserAnalysis';
import CommentAnalysis from './CommentAnalysis';
import IssueDetection from './IssueDetection';
import DataAnalytics from './DataAnalytics';
import axiosInstance from "../../../../utils/axiosInstance";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`twitter-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TwitterConnector = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/twitter/status');
      const response = res.data;
      if (response.success) {
        setConnectionStatus(response);
      }
    } catch (err) {
      setError('Failed to check Twitter connection');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await axiosInstance.get('/api/twitter/auth/start');
      const response = res.data;
      if (response.success && response.authorization_url) {
        window.location.href = response.authorization_url;
      }
    } catch (err) {
      showSnackbar('Failed to start authentication', 'error');
    }
  };

  const handleDisconnect = async () => {
    try {
      await axiosInstance.post('/api/twitter/disconnect');
      setConnectionStatus({ connected: false });
      showSnackbar('Disconnected from Twitter', 'success');
    } catch (err) {
      showSnackbar('Failed to disconnect', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!connectionStatus?.connected) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Connect Your Twitter/X Account
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 3 }}>
            Connect your Twitter account to start analyzing your posts and engagement.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleConnect}
            sx={{ backgroundColor: '#1DA1F2', '&:hover': { backgroundColor: '#1a8cd8' } }}
          >
            Connect Twitter
          </Button>
          
          <Box sx={{ mt: 4, textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>Features:</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>📊 Content Aggregation - Sync and manage all your posts</li>
              <li>👥 User Analysis - Track who engages with your content</li>
              <li>💭 Comment Analysis - AI-powered sentiment & intent detection</li>
              <li>⚠️ Issue Detection - Identify pain points automatically</li>
              <li>📈 Data Analytics - Comprehensive reports & exports</li>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <>
        <Paper
           sx={{
             p: 3,
             mt: 10,
             mx: "auto",
             maxWidth: "1200px",
             bgcolor: "background.paper",
           }}
         >
           <Typography variant="h5" component="h1" sx={{ mb: 3 }} color="textPrimary">
             Twitter <strong>Analysis</strong>
           </Typography>
           <Divider sx={{ mb: 3 }} />
   
           <Box>
        {/* Connection Status Header */}
        <TwitterConnectionStatus
            status={connectionStatus}
            onRefresh={checkConnectionStatus}
            onDisconnect={handleDisconnect}
        />

        {/* API Tier Warning */}
        {connectionStatus?.api_tier === 'free' && (
            <Alert severity="info" sx={{ mx: 3, mt: 2 }}>
            <strong>Free Tier:</strong> Limited to 10 posts/sync.
            </Alert>
        )}

        {/* Main Tabs */}
        <Paper sx={{ mx: 3, mt: 2 }}>
            <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
            <Tab icon={<ContentIcon />} label="Content" iconPosition="start" />
            <Tab icon={<UsersIcon />} label="Users" iconPosition="start" />
            <Tab icon={<SentimentIcon />} label="Sentiment" iconPosition="start" />
            <Tab icon={<IssuesIcon />} label="Issues" iconPosition="start" />
            <Tab icon={<AnalyticsIcon />} label="Analytics" iconPosition="start" />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
            <ContentAggregation 
                apiTier={connectionStatus?.api_tier}
                onShowSnackbar={showSnackbar}
            />
            </TabPanel>
            
            <TabPanel value={activeTab} index={1}>
            <UserAnalysis 
                apiTier={connectionStatus?.api_tier}
                onShowSnackbar={showSnackbar}
            />
            </TabPanel>
            
            <TabPanel value={activeTab} index={2}>
            <CommentAnalysis 
                apiTier={connectionStatus?.api_tier}
                onShowSnackbar={showSnackbar}
            />
            </TabPanel>
            
            <TabPanel value={activeTab} index={3}>
            <IssueDetection 
                apiTier={connectionStatus?.api_tier}
                onShowSnackbar={showSnackbar}
            />
            </TabPanel>
            
            <TabPanel value={activeTab} index={4}>
            <DataAnalytics 
                apiTier={connectionStatus?.api_tier}
                onShowSnackbar={showSnackbar}
            />
            </TabPanel>
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
            </Alert>
        </Snackbar>
        </Box>
    </Paper>
    </>
  );
};

export default TwitterConnector;